import React, { useCallback, useEffect, useState } from 'react';
import {
  CheckCircle2,
  Clock3,
  EyeOff,
  Loader2,
  MessageSquare,
  Send,
  UserRound,
} from 'lucide-react';
import { portalAdminService } from '../../services/api';

const fa = (n: number) => n.toLocaleString('fa-IR');

const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500';

type Status = 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';

const STATUS: Record<Status, { label: string; color: string; bg: string }> = {
  OPEN: { label: 'باز', color: '#B45309', bg: '#FEF8EA' },
  IN_PROGRESS: { label: 'در حال بررسی', color: '#1D4ED8', bg: '#EEF3FF' },
  RESOLVED: { label: 'حل شده', color: '#047857', bg: '#F1FAF1' },
  CLOSED: { label: 'بسته', color: '#64748B', bg: '#F1F5F9' },
};

interface Ticket {
  id: string;
  subject: string;
  body: string;
  status: Status;
  createdAt: string;
  participant: {
    id: string;
    cohort: { id: string; name: string } | null;
    user: { id: string; firstName?: string | null; lastName?: string | null; phone?: string | null };
  };
  assignee?: { id: string; firstName?: string | null; lastName?: string | null } | null;
  replies: Array<{
    id: string;
    body: string;
    isInternal: boolean;
    createdAt: string;
    author: { id: string; firstName?: string | null; lastName?: string | null };
  }>;
}

const nameOf = (u?: { firstName?: string | null; lastName?: string | null } | null) =>
  [u?.firstName, u?.lastName].filter(Boolean).join(' ') || '—';

/**
 * صف تیکت‌های پشتیبانی.
 *
 * بدون این تب، پیام مشتری در پرتال ثبت می‌شد ولی هیچ‌کس آن را نمی‌دید —
 * یعنی کاربر منتظر جوابی می‌ماند که هرگز نمی‌آمد.
 */
export const PortalTicketsTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [status, setStatus] = useState<Status | ''>('');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState('');
  const [reply, setReply] = useState('');
  const [internal, setInternal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTickets(
        await portalAdminService.listTickets({
          status: status || undefined,
          search: search.trim() || undefined,
        }),
      );
    } catch (err: any) {
      setError(err?.response?.data?.message || 'دریافت تیکت‌ها انجام نشد.');
    } finally {
      setLoading(false);
    }
  }, [status, search]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
      setError('');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'عملیات انجام نشد.');
    } finally {
      setBusy(false);
    }
  };

  const send = (ticketId: string) =>
    run(async () => {
      if (!reply.trim()) return;
      await portalAdminService.replyTicket(ticketId, reply.trim(), internal);
      setReply('');
      setInternal(false);
      await load();
    });

  const setTicketStatus = (ticketId: string, next: Status) =>
    run(async () => {
      await portalAdminService.updateTicket(ticketId, { status: next });
      await load();
    });

  return (
    <div className="space-y-4">
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
          {error}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white p-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="موضوع یا موبایل…"
          className={inputClass + ' w-auto min-w-[220px]'}
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as Status | '')}
          className={inputClass + ' w-auto'}
        >
          <option value="">باز و در حال بررسی</option>
          {(Object.keys(STATUS) as Status[]).map((key) => (
            <option key={key} value={key}>
              {STATUS[key].label}
            </option>
          ))}
        </select>
        <span className="text-sm font-bold text-gray-500">{fa(tickets.length)} تیکت</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white py-16 text-sm font-bold text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          در حال بارگذاری…
        </div>
      ) : (
        <div className="space-y-2">
          {tickets.map((ticket) => {
            const badge = STATUS[ticket.status];
            const isOpen = openId === ticket.id;
            const publicReplies = ticket.replies.filter((r) => !r.isInternal).length;

            return (
              <div key={ticket.id} className="rounded-xl border border-gray-200 bg-white">
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? '' : ticket.id)}
                  className="flex w-full items-center gap-3 p-4 text-right"
                >
                  <span
                    className="shrink-0 rounded-full px-2.5 py-1 text-[10.5px] font-black"
                    style={{ backgroundColor: badge.bg, color: badge.color }}
                  >
                    {badge.label}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-bold text-gray-900">
                      {ticket.subject}
                    </span>
                    <span className="mt-0.5 flex items-center gap-2 text-[11px] font-bold text-gray-400">
                      <UserRound className="h-3 w-3" />
                      {nameOf(ticket.participant.user)}
                      {ticket.participant.user.phone ? ' · ' + ticket.participant.user.phone : ''}
                      {ticket.participant.cohort ? ' · ' + ticket.participant.cohort.name : ''}
                    </span>
                  </span>
                  <span className="shrink-0 text-[11px] font-bold text-gray-400">
                    {publicReplies ? fa(publicReplies) + ' پاسخ' : 'بدون پاسخ'}
                  </span>
                  <span className="shrink-0 text-[10.5px] text-gray-400">
                    {new Date(ticket.createdAt).toLocaleDateString('fa-IR')}
                  </span>
                </button>

                {isOpen ? (
                  <div className="border-t border-gray-100 p-4">
                    <p className="whitespace-pre-line rounded-lg bg-gray-50 p-3 text-[12.5px] leading-7 text-gray-700">
                      {ticket.body}
                    </p>

                    <div className="mt-3 space-y-2">
                      {ticket.replies.map((r) => (
                        <div
                          key={r.id}
                          className={
                            'rounded-lg border p-3 ' +
                            (r.isInternal
                              ? 'border-amber-200 bg-amber-50'
                              : 'border-blue-100 bg-blue-50/40')
                          }
                        >
                          <p className="flex items-center gap-1.5 text-[10.5px] font-black text-gray-500">
                            {r.isInternal ? (
                              <>
                                <EyeOff className="h-3 w-3" />
                                یادداشت داخلی — برای مشتری نمایش داده نمی‌شود
                              </>
                            ) : (
                              <>
                                <MessageSquare className="h-3 w-3" />
                                پاسخ به مشتری
                              </>
                            )}
                            <span className="mr-auto font-bold text-gray-400">
                              {nameOf(r.author)} · {new Date(r.createdAt).toLocaleDateString('fa-IR')}
                            </span>
                          </p>
                          <p className="mt-1.5 whitespace-pre-line text-[12.5px] leading-7 text-gray-800">
                            {r.body}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className="mt-3 rounded-lg border border-gray-200 p-3">
                      <textarea
                        rows={3}
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        placeholder="پاسخ خود را بنویسید…"
                        className={inputClass}
                      />
                      <div className="mt-2 flex flex-wrap items-center gap-3">
                        <label className="flex cursor-pointer items-center gap-1.5 text-[11.5px] font-bold text-gray-600">
                          <input
                            type="checkbox"
                            checked={internal}
                            onChange={(e) => setInternal(e.target.checked)}
                          />
                          یادداشت داخلی تیم (مشتری نمی‌بیند)
                        </label>
                        <button
                          type="button"
                          disabled={busy || !reply.trim()}
                          onClick={() => send(ticket.id)}
                          className="mr-auto flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-40"
                        >
                          <Send className="h-4 w-4" />
                          ارسال
                        </button>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2 border-t border-gray-100 pt-3">
                      {(['IN_PROGRESS', 'RESOLVED', 'CLOSED'] as Status[])
                        .filter((s) => s !== ticket.status)
                        .map((s) => (
                          <button
                            key={s}
                            type="button"
                            disabled={busy}
                            onClick={() => setTicketStatus(ticket.id, s)}
                            className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-[11.5px] font-bold text-gray-700 disabled:opacity-50"
                          >
                            {s === 'RESOLVED' ? (
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            ) : (
                              <Clock3 className="h-3.5 w-3.5" />
                            )}
                            تغییر به «{STATUS[s].label}»
                          </button>
                        ))}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}

          {!tickets.length ? (
            <div className="rounded-xl border border-gray-200 bg-white py-16 text-center text-sm font-bold text-gray-400">
              تیکتی در این دسته نیست.
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
